import { colors } from '@naf-simba/theme';
import {
  NafThemeType,
  themeJustSuper as nafThemeJustSuper,
  themeLight as nafThemeLight,
  themeVisualBreak as nafThemeVisualBreak,
} from '@naf/theme';

/**
 * The NAF themes with the old green palette swapped for Simba's `secondary`.
 *
 * `@naf/theme` still carries the pre-Simba greens, and they reach the app in
 * two ways: through the theme a page is rendered with (the three themes below
 * use the greens differently — `themeVisualBreak` inverts them, `themeJustSuper`
 * is not green at all) and through `themeLight` imported directly as a fallback
 * for `props.theme`. Mapping the values here covers both, and keeps every token
 * doing what it did — `background.brand` is still the brand background, only in
 * the new colour.
 *
 * Per the design system's migration sheet:
 *
 * | Old green      | Simba          |
 * | -------------- | -------------- |
 * | Dugg  #E9F3E6  | secondary 50   |
 * | Mose  #D3EAC2  | secondary 100  |
 * | Kart  #7BDB83  | secondary 300  |
 * | Park  #267C4F  | secondary 500  |
 * | Gran  #062E2A  | secondary 900  |
 */
const SIMBA_SECONDARY_BY_OLD_GREEN: Record<string, string> = {
  '#e9f3e6': colors.secondary[50],
  '#d3eac2': colors.secondary[100],
  '#7bdb83': colors.secondary[300],
  '#267c4f': colors.secondary[500],
  '#062e2a': colors.secondary[900],
};

/** Every leaf in a NAF theme is a string, so this only has to walk objects. */
const migrateGreens = <T>(value: T): T => {
  if (typeof value === 'string') {
    return (SIMBA_SECONDARY_BY_OLD_GREEN[value.toLowerCase()] ?? value) as T;
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, leaf]) => [key, migrateGreens(leaf)])) as T;
  }

  return value;
};

export const themeLight: NafThemeType = migrateGreens(nafThemeLight);
export const themeVisualBreak: NafThemeType = migrateGreens(nafThemeVisualBreak);
export const themeJustSuper: NafThemeType = migrateGreens(nafThemeJustSuper);
